import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import useAuthContext from '../../hooks/useAuthContext';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { Title, Loader, DeviceFirmwareReleaseTypeSelect, DropdownSelect } from '../../elements';
import { DeviceFirmwareModal, HealthInjectorsPage } from '../../components';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_BUTTON_DEFAULT, V4_CHIP_DEFAULT, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { DeviceFirmwareType, DeviceFirmwareRfidType } from '../../models/device';
import { useTranslation } from 'react-i18next';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import {
  DeviceUploadDeviceFirmwarePayload,
  HealthSystemDALEnumsDeviceFirmwareType,
  HealthSystemDomainDeviceResultDeviceFirmwareViewModel
} from '../../api/HealthSystemApi';

const DeviceSettings: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();
  const confirmDialog = useConfirmation();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const [uploadFirmwareModalOpened, setUploadFirmwareModalOpened] = React.useState(false);

  const handleRemoveDeviceFirmware = (firmware: HealthSystemDomainDeviceResultDeviceFirmwareViewModel) => {
    const firmwareText = `Version ${firmware.version} for ${
      DeviceFirmwareType[firmware.deviceFirmwareType]
    } from ${new Date(firmware.dateTime).toLocaleString()}`;

    confirmDialog({
      dialogId: 'del-all-confirm',
      title: t('f-device-del-title'),
      message: t('f-device-del-info', { firmwareText }),
      alertSeverity: 'warning'
    })
      .then(() => {
        apiService.api.device
          .deviceRemoveDeviceFirmware({ Id: firmware.id })
          .then(() => {
            notifications.success(t('f-device-del-ok'));
            reloadSectionByType(firmware.deviceFirmwareType);
          })
          .catch((e) => {
            notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
          });
      })
      .catch(() => {});
  };

  const openUploadFirmwareDialog = () => {
    setUploadFirmwareModalOpened(true);
  };

  const handleUploadFirmwareDialogCLose = (payload: DeviceUploadDeviceFirmwarePayload | null) => {
    setUploadFirmwareModalOpened(false);

    if (payload === null) {
      return;
    }

    apiService.api.device
      .deviceUploadDeviceFirmware(payload)
      .then(() => {
        notifications.success(t('f-device-add-ok'));
        reloadSectionByType(payload.Type);
      })
      .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })));
  };

  const firmwareSection_ApplicationRef = React.useRef<HTMLElement>(null!);
  const firmwareSection_BootloaderRef = React.useRef<HTMLElement>(null!);
  const firmwareSection_BluetoothRef = React.useRef<HTMLElement>(null!);
  const firmwareSection_RfidRef = React.useRef<HTMLElement>(null!);

  const reloadSectionByType = (type: HealthSystemDALEnumsDeviceFirmwareType | undefined) => {
    if (type === undefined) {
      return;
    }

    let ref: any;
    if (type === DeviceFirmwareType.Application) ref = firmwareSection_ApplicationRef.current;
    if (type === DeviceFirmwareType.Bootloader) ref = firmwareSection_BootloaderRef.current;
    if (type === DeviceFirmwareType.Bluetooth) ref = firmwareSection_BluetoothRef.current;
    if (type === DeviceFirmwareType.RFID) ref = firmwareSection_RfidRef.current;

    ref.reload();
  };

  return (
    <Grid container spacing={2}>
      <Grid item md={12}>
        <HealthInjectorsPage />
      </Grid>

      <Grid item md={12}>
        <Divider />
      </Grid>

      <Grid item container md={6} spacing={1}>
        <Grid item>
          <Icon color="primary" fontSize="large">
            {'build'}
          </Icon>
        </Grid>
        <Grid item>
          <Title>{t('f-settings-device-title')}</Title>
        </Grid>
      </Grid>

      <Grid item container md={6} sx={styles.justifyRight}>
        <Grid item>
          <Button
            size="small"
            disabled={state.role !== UserRole.admin}
            color="inherit"
            startIcon={<Icon>{'cloud_upload'}</Icon>}
            onClick={openUploadFirmwareDialog}
            variant="contained"
            sx={V4_BUTTON_DEFAULT}
          >
            {t('f-com-upload-new')}
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item md={12}>
            <DeviceSettingsViewTable
              ref={firmwareSection_ApplicationRef}
              firmwareType={DeviceFirmwareType.Application}
              removeDeviceFirmware={handleRemoveDeviceFirmware}
            />
          </Grid>

          <Grid item md={12}>
            <DeviceSettingsViewTable
              ref={firmwareSection_BootloaderRef}
              firmwareType={DeviceFirmwareType.Bootloader}
              removeDeviceFirmware={handleRemoveDeviceFirmware}
            />
          </Grid>

          <Grid item md={12}>
            <DeviceSettingsViewTable
              ref={firmwareSection_BluetoothRef}
              firmwareType={DeviceFirmwareType.Bluetooth}
              removeDeviceFirmware={handleRemoveDeviceFirmware}
            />
          </Grid>

          <Grid item md={12}>
            <DeviceSettingsViewTable
              ref={firmwareSection_RfidRef}
              firmwareType={DeviceFirmwareType.RFID}
              removeDeviceFirmware={handleRemoveDeviceFirmware}
            />
          </Grid>
        </Grid>
      </Grid>

      <DeviceFirmwareModal opened={uploadFirmwareModalOpened} onModalAction={handleUploadFirmwareDialogCLose} />
    </Grid>
  );
};

export default DeviceSettings;

interface ITableProps {
  firmwareType: DeviceFirmwareType;
  removeDeviceFirmware: (firmware: HealthSystemDomainDeviceResultDeviceFirmwareViewModel) => void;
}

// device firmware section component
// separated by device firmware type
const DeviceSettingsViewTable = React.forwardRef(({ firmwareType, removeDeviceFirmware }: ITableProps, ref) => {
  const styles = sxStyles(useTheme());
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const apiService = new ApiService();

  let sectionIcon = 'system_update';
  let sectionText = 'f-device-app';

  if (firmwareType === DeviceFirmwareType.Bluetooth) {
    sectionIcon = 'bluetooth';
    sectionText = 'f-device-bt';
  } else if (firmwareType === DeviceFirmwareType.Bootloader) {
    sectionIcon = 'memory';
    sectionText = 'f-device-boot';
  } else if (firmwareType === DeviceFirmwareType.RFID) {
    sectionIcon = 'settings_input_antenna';
    sectionText = 'f-device-rfid';
  }

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const renderFirmwareTableBody = () => {
    return deviceFirmware.map((row) => (
      <TableRow hover key={row.id}>
        <TableCell align="right">
          <Typography variant="caption">{row.id}</Typography>
        </TableCell>
        <TableCell>{row.version}</TableCell>
        <TableCell>{row.fileName}</TableCell>
        <TableCell>{new Date(row.dateTime).toLocaleString()}</TableCell>
        <TableCell>{row.info}</TableCell>
        <TableCell>
          <Tooltip arrow title={t('f-device-del')}>
            <IconButton
              size="small"
              disabled={state.role !== UserRole.admin}
              onClick={() => removeDeviceFirmware(row)}
              color="primary"
              sx={V4_ICON_BUTTON_SMALL}
            >
              <Icon>{'delete_outline'}</Icon>
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  const renderEmptyTableRow = () => {
    return (
      <TableRow>
        <TableCell variant="footer" align="right">
          <Chip sx={{ ...V4_CHIP_DEFAULT, ...styles.tableInfoChip }} label={t('f-device-empty')} />
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
      </TableRow>
    );
  };

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [deviceFirmware, setDeviceFirmware] = React.useState<HealthSystemDomainDeviceResultDeviceFirmwareViewModel[]>(
    []
  );
  const [releaseType, setReleaseType] = React.useState('');
  const [rfidType, setRfidType] = React.useState('');

  const loadDeviceFirmware = async (firmwareType: DeviceFirmwareType, releaseType: string) => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.device
      .deviceGetDeviceFirmwareVersions({
        Page: searchPage,
        Size: rowsPerPage,
        Type: firmwareType,
        ReleaseType: releaseType,
        RfidType: rfidType
      })
      .then((result) => {
        setDeviceFirmware(result.data.data);
        setTotal(result.data.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(t('f-device-load-error', { error: e.toString() }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadDeviceFirmware(firmwareType, releaseType);
      loadData();
    }
  }, [loaded]);

  //external reload call
  React.useImperativeHandle(ref, () => ({
    reload() {
      setLoaded(false);
    }
  }));

  return (
    <Card elevation={1}>
      <CardHeader
        sx={styles.cardHeader}
        avatar={<Icon color="primary">{sectionIcon}</Icon>}
        title={
          <>
            {t(sectionText)} <Chip label={total} variant="outlined" size="small" />{' '}
          </>
        }
      />

      <CardContent sx={styles.cardContent}>
        <Grid container spacing={1} sx={styles.withMargin}>
          <Grid item md={2} xs={4}>
            <DeviceFirmwareReleaseTypeSelect
              value={releaseType}
              cssClass="!m-2"
              fullWidth
              onChange={(value) => {
                setReleaseType(value);
                setLoaded(false);
              }}
            />
          </Grid>

          {(firmwareType === DeviceFirmwareType.Application || firmwareType === DeviceFirmwareType.RFID) && (
            <Grid item md={2} xs={4}>
              <DropdownSelect
                items={[
                  { key: t('f-any'), value: '' },
                  { key: t('f-device-rfid-type-lf'), value: DeviceFirmwareRfidType[DeviceFirmwareRfidType.LF] },
                  { key: t('f-device-rfid-type-uhf'), value: DeviceFirmwareRfidType[DeviceFirmwareRfidType.UHF] }
                ]}
                value={rfidType}
                label={t('f-device-rfid-type')}
                cssClass="!m-2"
                fullWidth
                onChange={(value) => {
                  setRfidType(value);
                  setLoaded(false);
                }}
              />
            </Grid>
          )}
        </Grid>

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {loaded && (
          <Table aria-label="firmware list" size="small" sx={V4_TABLE}>
            <TableHead>
              <TableRow>
                <TableCell align="right">{'ID'}</TableCell>
                <TableCell>{t('f-device-ver')}</TableCell>
                <TableCell>{t('f-device-file')}</TableCell>
                <TableCell>{t('f-com-date-time')}</TableCell>
                <TableCell>{t('f-com-info')}</TableCell>
                <TableCell>{''}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderFirmwareTableBody()}

              {deviceFirmware.length === 0 && renderEmptyTableRow()}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </CardContent>
    </Card>
  );
});

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    cardHeader: {
      paddingBottom: 0,
      paddingTop: '5px',
      paddingLeft: '5px',
      background: colors.defaultGrey
    },
    cardFooter: {
      justifyContent: 'flex-end'
    },
    tableHeaderFooter: {
      background: colors.defaultGrey
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    justifyRight: {
      justifyContent: 'flex-end'
    },
    withMargin: {
      margin: theme.spacing(1)
    },
    cardContent: {
      padding: 0,
      '&:last-child': {
        paddingBottom: 0
      }
    }
  }) as const;
